import { Box, Flex, VStack } from '@chakra-ui/react';
import { createRichTextHtmlResolver } from '@kontent-ai/delivery-sdk';
import { nodeParser } from '@kontent-ai/delivery-node-parser';
import { colorScheme } from 'lib/colorScheme';
import React, { FunctionComponent, useState } from 'react';
import { moduleMaxWidth } from 'themes/overrides';
import { InbuiltFormData } from 'utils/types/formData';
import { InbuiltForm as InbuiltFormModal } from '../../models/content-types/inbuilt_form';
import { FormWrapper } from './FormWrapper';

interface InbuiltFormProps {
  model: InbuiltFormModal;
  onSubmit?: () => void;
}

export const InbuiltForm: FunctionComponent<InbuiltFormProps> = ({ model, onSubmit }) => {
  const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

  // Extracting form data from JSON
  const formDataObj = JSON.parse(model.elements.hubspotFormSnippetForm.value);

  // Background color logic
  const backgroundColor = model.elements.backgroundColor.value;
  const bgColor = colorScheme[backgroundColor[0].codename as keyof typeof colorScheme].base;

  const textbg = model.elements.textBesideBackground?.value[0]?.codename;
  const isFormOnlyBackground = textbg === 'only_form';

  // Rich headline and title
  const richHeadlineHtml = createRichTextHtmlResolver(nodeParser).resolveRichText({
    element: model.elements.headlineFlexible,
  }).html;

  const richTitleHtml = createRichTextHtmlResolver(nodeParser).resolveRichText({
    element: model.elements.richTitle,
  }).html;

  // Form data object
  const data: InbuiltFormData = {
    formId: formDataObj.FormGUID,
    portalId: formDataObj.PortalID,
    headline: richHeadlineHtml,
    backgroundColor: bgColor,
    submittedMessageAlignment: 'center',
    fieldColor: bgColor === 'white' ? 'gray.98' : 'white',
  };

  return (
    <VStack
      w='100vw'
      maxW={moduleMaxWidth}
      mx='auto'
      align='stretch'
      id={model.system.codename}
      bg={isFormOnlyBackground ? 'white' : bgColor}
      px={{ base: 'xs', md: 'l' }}
      spacing={{ base: 's', md: 'm' }}
      mt='10px'
    >
      <Flex
        alignItems={'center'}
        w='100%'
        flexDir='column'
        p={{ base: 's', lg: 'l' }}
        bg={isFormOnlyBackground ? bgColor : 'transparent'}
        borderRadius='md'
      >
        <Flex gap={{ base: 's', lg: 'm' }} flexDir='column' w={{ base: '100%', lg: '50%', xl: '60%' }}>
          {/* Rich Title */}
          {richTitleHtml && (
            <Box
              textAlign={'center'}
              textStyle={{ base: 'mobile.h4', lg: 'desktop.h4' }}
              color='#273943'
              dangerouslySetInnerHTML={{ __html: richTitleHtml }}
            />
          )}

          {/* Rich Headline */}
          {!hasBeenSubmitted && richHeadlineHtml && (
            <Box
              textAlign={'center'}
              textStyle={{ base: 'mobile.h4', lg: 'desktop.h4' }}
              color='#0098EB'
              dangerouslySetInnerHTML={{ __html: richHeadlineHtml }}
            />
          )}

          {/* Form */}
          <FormWrapper
            data={data}
            onSubmit={() => {
              setHasBeenSubmitted(true);
              if (onSubmit) {
                onSubmit();
              }
            }}
          />
        </Flex>
      </Flex>
    </VStack>
  );
};
